import React, { useMemo, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import {
  actionFieldInputClassName,
  actionListboxButtonClassName,
  actionListboxOptionActiveClassName,
  actionTextClassName,
} from '@core/theme/actionTheme.util';

export interface ActionListboxOption {
  label: string;
  value: string;
  searchText?: string;
}

interface ActionListboxFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: ActionListboxOption[];
  placeholder: string;
  noneOptionLabel?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  noMatchesLabel?: string;
}

const getOptionClassName = ({ active }: { active: boolean }) =>
  `relative cursor-default py-2 pr-4 pl-10 select-none ${
    active ? actionListboxOptionActiveClassName : 'text-gray-900'
  }`;

const OptionContent = ({
  label,
  selected,
}: {
  label: string;
  selected: boolean;
}) => (
  <>
    <span
      className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
    >
      {label}
    </span>
    {selected ? (
      <span
        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${actionTextClassName}`}
      >
        <FontAwesomeIcon icon={faCheck} />
      </span>
    ) : null}
  </>
);

const ActionListboxField: React.FC<ActionListboxFieldProps> = ({
  id,
  value,
  onChange,
  options,
  placeholder,
  noneOptionLabel,
  searchable = false,
  searchPlaceholder = 'Search...',
  noMatchesLabel = 'No matches found',
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const selectedOption = options.find((option) => option.value === value);
  const trimmedQuery = searchable ? searchQuery.trim().toLowerCase() : '';

  const filteredOptions = useMemo(() => {
    if (!trimmedQuery) return options;

    return options.filter((option) =>
      `${option.label} ${option.searchText ?? option.value}`
        .toLowerCase()
        .includes(trimmedQuery),
    );
  }, [options, trimmedQuery]);

  const showNoneOption =
    !!noneOptionLabel &&
    (!trimmedQuery || noneOptionLabel.toLowerCase().includes(trimmedQuery));

  return (
    <Listbox value={value} onChange={onChange}>
      <div className="relative">
        <Listbox.Button id={id} className={actionListboxButtonClassName}>
          <span className="block truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </span>
        </Listbox.Button>
        {/* The panel is positioned inline rather than anchored, so it stays in
            the stacking context of any fixed modal hosting the field. */}
        <Transition
          as={React.Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setSearchQuery('')}
        >
          <Listbox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
            {searchable ? (
              <li className="sticky top-0 z-10 bg-white px-2 py-1.5 ring-1 ring-black/5">
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={(event) => event.stopPropagation()}
                  className={`w-full px-2 py-1.5 text-sm ${actionFieldInputClassName}`}
                />
              </li>
            ) : null}
            {showNoneOption ? (
              <Listbox.Option className={getOptionClassName} value="">
                {({ selected }) => (
                  <OptionContent
                    label={noneOptionLabel ?? ''}
                    selected={selected}
                  />
                )}
              </Listbox.Option>
            ) : null}
            {!filteredOptions.length && !showNoneOption ? (
              <li className="px-4 py-2 text-gray-500">{noMatchesLabel}</li>
            ) : null}
            {filteredOptions.map((option) => (
              <Listbox.Option
                key={option.value}
                className={getOptionClassName}
                value={option.value}
              >
                {({ selected }) => (
                  <OptionContent label={option.label} selected={selected} />
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
};

export default ActionListboxField;
